/**
 * Text, select, checkbox and textarea controls.
 *
 * Deliberately unlabelled: labelling, hints, errors and the `aria-describedby`
 * wiring all live in `core-forms`' `Field`, which passes them in. Splitting it
 * this way means there is exactly one implementation of the accessibility
 * wiring rather than one per control.
 */

import {
  forwardRef,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";

import "./controls.css";

export type InputProps = InputHTMLAttributes<HTMLInputElement>;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={["hardauto-input", className].filter(Boolean).join(" ")}
      {...rest}
    />
  );
});

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, rows = 4, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={["hardauto-input", "hardauto-textarea", className].filter(Boolean).join(" ")}
      {...rest}
    />
  );
});

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, children, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      className={["hardauto-input", "hardauto-select", className].filter(Boolean).join(" ")}
      {...rest}
    >
      {children}
    </select>
  );
});

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: string;
}

/**
 * A checkbox, which unlike the others carries its own label.
 *
 * The label sits beside the control rather than above it, so `Field`'s layout
 * does not fit; wrapping the input in the `<label>` binds the two without an
 * id and gives the text a click target.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, className, ...rest },
  ref,
) {
  return (
    <label className={["hardauto-checkbox", className].filter(Boolean).join(" ")}>
      <input ref={ref} type="checkbox" {...rest} />
      <span>{label}</span>
    </label>
  );
});
